import React, { useMemo, useState, useCallback, useRef, useEffect } from "react";
import {
    Container,
    Row,
    Col,
    Form,
    Card,
    Alert,
    Table,
    Pagination,
    Button,
} from "react-bootstrap";
import { Chart } from "react-google-charts";
import { FaChevronUp, FaChevronDown, FaQuestionCircle } from "react-icons/fa";
import html2canvas from "html2canvas";
import {
    useGetShotDataQuery,
    useGetPlayerSummaryQuery,
    useGetPlayerGamesStatsQuery,
} from "../slices/shotDataApiSlice";
import { useGetGameDataQuery } from "../slices/gameDataApiSlice";
import Loader from "../components/Loader";
import Message from "../components/Message";
import ShotMapCanvas from "../components/ShotMapCanvas";
import ShotMapLegend from "../components/ShotMapLegend";
import ScreenTabs from "../components/ScreenTabs";
import { useShotMarkers } from "../hooks/useShotMarkers";

const ANALYSIS_TABS = [
    { label: "Game Analysis", to: "/ShotAnalysisScreen" },
    { label: "Player Analysis", to: "/PlayerAnalysisScreen" },
    { label: "Season Analysis", to: "/SeasonAnalysisScreen" },
];

function capitalize(str) {
    if (str === "") {
        return "";
    }

    return str.slice(0, 1).toUpperCase() + str.slice(1);
}

function sortByPreferredOrder(values, preferredOrder) {
    const seen = new Set();
    const out = [];
    preferredOrder.forEach((v) => {
        if (values.includes(v) && !seen.has(v)) {
            out.push(v);
            seen.add(v);
        }
    });
    values.forEach((v) => {
        if (!seen.has(v)) out.push(v);
    });
    return out;
}

function formatShotTypeLabel(value) {
    if (value === "All") return value;
    return String(value || "").replace(/\s*attempt$/i, "");
}

const PlayerAnalysisScreen = () => {
    // columns for player summary table
    const columns = [
        "player",
        "attempts",
        "scored",
        "points",
        "xPoints",
        "efficiency",
        "conversionRate",
    ];

    // header labels for player summary table
    const headerLabels = {
        player: "Player",
        attempts: "Shots",
        scored: "Scored",
        points: "Points",
        xPoints: "xPoints",
        efficiency: "Efficiency",
        conversionRate: "Conversion",
    };

    // local state
    const [seasonPlayer, setSeasonPlayer] = useState("All");
    const [openSeason, setOpenSeason] = useState(true);
    const [summaryOpen, setSummaryOpen] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [sortField, setSortField] = useState("player");
    const [sortDirection, setSortDirection] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [chartWrapper, setChartWrapper] = useState(null);
    const [mapOpen, setMapOpen] = useState(true);
    const [mapShotType, setMapShotType] = useState("All");
    const [mapGame, setMapGame] = useState("All");
    const [mapGameHalf, setMapGameHalf] = useState("All");
    const [mapSetPlay, setMapSetPlay] = useState("All");
    const [mapShotOutcome, setMapShotOutcome] = useState("All");
    const [mapShotPressure, setMapShotPressure] = useState("All");
    const [mapShotMethod, setMapShotMethod] = useState("All");
    const mapExportRef = useRef(null);
    const pageTitleRef = useRef(null);
    const summaryCardRef = useRef(null);
    const noPlayerDataRef = useRef(null);
    const firstPlayerRowRef = useRef(null);
    const gameSummaryCardRef = useRef(null);
    const noSelectionAlertRef = useRef(null);
    const mapCardRef = useRef(null);
    const seasonChartCardRef = useRef(null);
    const [showTutorial, setShowTutorial] = useState(false);
    const [tutorialStepIndex, setTutorialStepIndex] = useState(0);
    const [tutorialRect, setTutorialRect] = useState(null);
    const [tutorialAutoSelectedPlayer, setTutorialAutoSelectedPlayer] =
        useState(false);

    const [gameSummaryOpen, setGameSummaryOpen] = useState(true);
    const [gamePage, setGamePage] = useState(1);
    const [gameLimit, setGameLimit] = useState(10);
    const [gameSearch, setGameSearch] = useState("");
    const [gameSortField, setGameSortField] = useState("gameDate");
    const [gameSortDirection, setGameSortDirection] = useState(-1);

    // -------------- shot data ---------------
    const {
        data: shotDataResponse = { entries: [] },
        isLoading: shotsLoading,
        error: shotsError,
    } = useGetShotDataQuery(
        { page: 1, limit: 10000 },
        { refetchOnMountOrArgChange: true },
    );

    // ---------------------- game data -------------------
    const { data: gamesDataResponse = { entries: [] } } = useGetGameDataQuery({
        page: 1,
        limit: 1000,
    });

    //------------- summary data --------------------
    const {
        data: playerSummaryResponse = [],
        isLoading: summaryLoading,
        error: summaryError,
    } = useGetPlayerSummaryQuery({
        limit: pageSize,
        page: currentPage,
        sort: sortField,
        order: sortDirection,
        search: searchQuery,
    });

    const {
        data: playerGamesResponse = {
            entries: [],
            total: 0,
            page: 1,
            pages: 1,
        },
        isLoading: gamesLoading,
        error: gamesError,
    } = useGetPlayerGamesStatsQuery(
        {
            playerName: seasonPlayer,
            page: gamePage,
            limit: gameLimit,
            search: gameSearch,
            sort: gameSortField,
            order: gameSortDirection,
        },
        { skip: seasonPlayer === "All" },
    );

    const playerData = useMemo(
        () => playerSummaryResponse?.summary || [],
        [playerSummaryResponse],
    );
    const totalRows = useMemo(
        () => playerSummaryResponse?.totalRows || 1,
        [playerSummaryResponse],
    );

    // helpers
    const normalize = useCallback(
        (value) =>
            typeof value === "string" ? value.trim().toLowerCase() : "",
        [],
    );

    const outcomePoints = useCallback(
        (outcome) => {
            const normalizedOutcome = normalize(outcome);
            if (normalizedOutcome === "goal") return 3;
            if (normalizedOutcome === "2 point") return 2;
            if (normalizedOutcome === "point") return 1;
            return 0;
        },
        [normalize],
    );

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection * -1);
        } else {
            setSortField(field);
            setSortDirection(1);
        }
    };

    const handleSelectPlayer = (playerName) => {
        setSeasonPlayer(playerName);
        setGamePage(1);
        setGameSearch("");
    };

    const handleGameSort = (field) => {
        if (gameSortField === field) {
            setGameSortDirection((prev) => prev * -1);
        } else {
            setGameSortField(field);
            setGameSortDirection(1);
        }
    };

    const formatGameDate = (value) => {
        if (!value) return "-";
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return String(value);
        return date.toLocaleDateString("en-IE");
    };

    // filters
    const allShots = shotDataResponse.entries;
    const teamShots = useMemo(() => allShots, [allShots]);
    const playerShots = useMemo(
        () =>
            seasonPlayer === "All"
                ? teamShots
                : teamShots.filter((shot) => shot.playerName === seasonPlayer),
        [teamShots, seasonPlayer],
    );

    const mapGameOptions = useMemo(() => {
        const ids = new Set(
            playerShots
                .map((shot) => shot.game || shot.gameId)
                .filter((gameId) => Boolean(gameId)),
        );
        return ["All", ...Array.from(ids)];
    }, [playerShots]);

    const mapShotTypeOptions = useMemo(
        () => ["All", ...new Set(playerShots.map((shot) => shot.shotType))],
        [playerShots],
    );
    const orderedMapShotTypeOptions = useMemo(
        () =>
            sortByPreferredOrder(mapShotTypeOptions, [
                "All",
                "Goal attempt",
                "Point attempt",
                "2 Point attempt",
            ]),
        [mapShotTypeOptions],
    );

    const mapSetPlayOptions = useMemo(
        () => ["All", ...new Set(playerShots.map((shot) => shot.setPlay))],
        [playerShots],
    );

    const mapShotOutcomeOptions = useMemo(
        () => ["All", ...new Set(playerShots.map((shot) => shot.shotOutcome))],
        [playerShots],
    );

    const mapShotPressureOptions = useMemo(
        () => ["All", ...new Set(playerShots.map((shot) => shot.shotPressure))],
        [playerShots],
    );

    const mapShotMethodOptions = useMemo(
        () => ["All", ...new Set(playerShots.map((shot) => shot.shotMethod))],
        [playerShots],
    );
    const orderedMapShotPressureOptions = useMemo(
        () =>
            sortByPreferredOrder(mapShotPressureOptions, [
                "All",
                "Low",
                "Medium",
                "High",
            ]),
        [mapShotPressureOptions],
    );
    const orderedMapShotMethodOptions = useMemo(
        () =>
            sortByPreferredOrder(mapShotMethodOptions, [
                "All",
                "Hand",
                "Left foot",
                "Right foot",
            ]),
        [mapShotMethodOptions],
    );

    const filteredPlayerShots = useMemo(
        () =>
            playerShots.filter((shot) => {
                const gameId = shot.game || shot.gameId;
                if (mapShotType !== "All" && shot.shotType !== mapShotType)
                    return false;
                if (mapGame !== "All" && gameId !== mapGame) return false;
                if (
                    mapGameHalf !== "All" &&
                    String(shot.gameHalf) !== mapGameHalf
                )
                    return false;
                if (mapSetPlay !== "All" && shot.setPlay !== mapSetPlay)
                    return false;
                if (
                    mapShotOutcome !== "All" &&
                    shot.shotOutcome !== mapShotOutcome
                )
                    return false;
                if (
                    mapShotPressure !== "All" &&
                    shot.shotPressure !== mapShotPressure
                )
                    return false;
                if (
                    mapShotMethod !== "All" &&
                    shot.shotMethod !== mapShotMethod
                )
                    return false;
                return true;
            }),
        [
            playerShots,
            mapShotType,
            mapGame,
            mapGameHalf,
            mapSetPlay,
            mapShotOutcome,
            mapShotPressure,
            mapShotMethod,
        ],
    );

    const { markers: mapMarkers, summary: mapSummary } =
        useShotMarkers(filteredPlayerShots);

    const handleDownloadPlayerMap = async () => {
        if (!mapExportRef.current) return;
        const canvas = await html2canvas(mapExportRef.current, {
            backgroundColor: null,
            scale: 2,
        });
        const link = document.createElement("a");
        const safePlayer = String(seasonPlayer || "player")
            .trim()
            .replace(/[^\w-]+/g, "_");
        link.download = `player-shot-map-${safePlayer}.png`;
        link.href = canvas.toDataURL("image/png");
        link.click();
    };

    // game labels
    const gameMap = useMemo(() => {
        const map = {};
        gamesDataResponse.entries.forEach((game) => {
            const label = `${game.HomeTeam} vs ${game.AwayTeam}`;
            map[game._id] = label;
        });
        return map;
    }, [gamesDataResponse]);

    // series definition
    const categories = useMemo(
        () => [
            {
                filter: (shot) => normalize(shot.shotType) === "goal attempt",
                label: "Goal",
            },
            {
                filter: (shot) =>
                    normalize(shot.shotType) === "2 point attempt",
                label: "2 Point",
            },
            {
                filter: (shot) =>
                    normalize(shot.shotType) === "point attempt" &&
                    normalize(shot.setPlay) !== "open play",
                label: "Point Dead Ball",
            },
            {
                filter: (shot) =>
                    normalize(shot.shotType) === "point attempt" &&
                    normalize(shot.setPlay) === "open play",
                label: "Point Open Play",
            },
        ],
        [normalize],
    );

    // prepare API data for displaying in table
    const playerSummary = useMemo(() => {
        const rows = playerData.map((summaryRow) => ({
            ...summaryRow,
            xPoints: Number(Number(summaryRow.xPoints || 0).toFixed(2)),
            efficiency: Number(Number(summaryRow.efficiency || 0).toFixed(2)),
            conversionRate: summaryRow.attempts > 0 ? `${Math.round((summaryRow.scored / summaryRow.attempts) * 100)}%` : "",
        }));
        return rows;
    }, [playerData]);
    const hasTutorialPlayersAvailable = playerSummary.length > 0;

    const tutorialSteps = useMemo(() => {
        const baseSteps = [
            {
                title: "Player Analysis Overview",
                body: "This screen focuses on individual-player analysis in Gaelic football. It lets you compare player efficiency, review game-by-game output, and inspect shot locations and season trends.",
                ref: pageTitleRef,
            },
            {
                title: "Player Summary",
                body: "This is the starting table. Use it to sort, search, and identify which player you want to analyse in more detail.",
                ref: summaryCardRef,
            },
        ];

        if (!hasTutorialPlayersAvailable) {
            return [
                ...baseSteps,
                {
                    title: "No Player Data Yet",
                    body: "There is no player shot data available yet. For a new user, this is expected until games and shots have been added and assigned to players.",
                    ref: noPlayerDataRef,
                },
                {
                    title: "What Unlocks Next",
                    body: "Once player shot data exists, this screen unlocks game-by-game summaries, a player shot map, and a season chart. Those sections stay in a waiting state until a player can be selected.",
                    ref: noSelectionAlertRef,
                },
            ];
        }

        return [
            ...baseSteps,
            {
                title: "Select a Player",
                body: "Choose a player from the summary table to drive the rest of the page. The lower panels all update from the selected player.",
                ref: firstPlayerRowRef,
            },
            {
                title: "Game Summary",
                body: "This section shows the selected player's output game by game, so you can review consistency, volume, and scoring efficiency across matches.",
                ref: gameSummaryCardRef,
            },
            {
                title: "Player Shot Map",
                body: "This shot map shows where the selected player takes their chances from. In Gaelic football, it helps reveal central shooting, wide shooting, and shot-selection patterns.",
                ref: mapCardRef,
            },
            {
                title: "Player Season Chart",
                body: "The season chart tracks how the player's shot efficiency changes across games. It is useful for spotting form trends and differences by shot type.",
                ref: seasonChartCardRef,
            },
        ];
    }, [hasTutorialPlayersAvailable]);

    useEffect(() => {
        if (!showTutorial) return;
        if (tutorialStepIndex > tutorialSteps.length - 1) {
            setTutorialStepIndex(Math.max(0, tutorialSteps.length - 1));
        }
    }, [showTutorial, tutorialStepIndex, tutorialSteps]);

    useEffect(() => {
        if (!showTutorial) return;
        const needsSelectedPlayer = hasTutorialPlayersAvailable && tutorialStepIndex >= 3;
        if (needsSelectedPlayer && seasonPlayer === "All" && playerSummary.length > 0) {
            setSeasonPlayer(playerSummary[0].player);
            setGamePage(1);
            setGameSearch("");
            setTutorialAutoSelectedPlayer(true);
        }
        if (hasTutorialPlayersAvailable && tutorialStepIndex >= 3) setGameSummaryOpen(true);
        if (hasTutorialPlayersAvailable && tutorialStepIndex >= 4) setMapOpen(true);
        if (hasTutorialPlayersAvailable && tutorialStepIndex >= 5) setOpenSeason(true);
    }, [
        showTutorial,
        tutorialStepIndex,
        hasTutorialPlayersAvailable,
        seasonPlayer,
        playerSummary,
    ]);

    const updateTutorialRect = useCallback(() => {
        if (!showTutorial) {
            setTutorialRect(null);
            return;
        }
        const step = tutorialSteps[tutorialStepIndex];
        const node = step?.ref?.current;
        if (!node) {
            setTutorialRect(null);
            return;
        }
        const rect = node.getBoundingClientRect();
        setTutorialRect({
            top: Math.max(8, rect.top - 8),
            left: Math.max(8, rect.left - 8),
            width: rect.width + 16,
            height: rect.height + 16,
        });
    }, [showTutorial, tutorialStepIndex, tutorialSteps]);

    useEffect(() => {
        if (!showTutorial) return undefined;
        const step = tutorialSteps[tutorialStepIndex];
        step?.ref?.current?.scrollIntoView({
            behavior: "smooth",
            block: tutorialStepIndex >= 3 ? "start" : "center",
        });
        const refresh = () => updateTutorialRect();
        refresh();
        const timeoutId = window.setTimeout(refresh, 220);
        const secondTimeoutId = window.setTimeout(refresh, 420);
        const intervalId = window.setInterval(refresh, 70);
        const stopIntervalId = window.setTimeout(() => {
            window.clearInterval(intervalId);
        }, 800);
        window.addEventListener("resize", refresh);
        window.addEventListener("scroll", refresh, true);
        return () => {
            window.clearTimeout(timeoutId);
            window.clearTimeout(secondTimeoutId);
            window.clearTimeout(stopIntervalId);
            window.clearInterval(intervalId);
            window.removeEventListener("resize", refresh);
            window.removeEventListener("scroll", refresh, true);
        };
    }, [showTutorial, tutorialStepIndex, tutorialSteps, updateTutorialRect]);

    const startTutorial = () => {
        setTutorialStepIndex(0);
        setTutorialAutoSelectedPlayer(false);
        setShowTutorial(true);
    };

    const closeTutorial = () => {
        if (tutorialAutoSelectedPlayer) {
            setSeasonPlayer("All");
            setGamePage(1);
            setGameSearch("");
            setTutorialAutoSelectedPlayer(false);
        }
        setShowTutorial(false);
        setTutorialRect(null);
    };

    const nextTutorialStep = () => {
        if (tutorialStepIndex >= tutorialSteps.length - 1) {
            closeTutorial();
            return;
        }
        setTutorialStepIndex((prev) => prev + 1);
    };

    const prevTutorialStep = () => {
        setTutorialStepIndex((prev) => Math.max(prev - 1, 0));
    };

    const getTutorialCardStyle = () => {
        if (!tutorialRect) return {};
        const cardWidth = Math.min(360, window.innerWidth - 32);
        const gap = 16;
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const placeBelow =
            tutorialRect.top + tutorialRect.height + 220 + gap < viewportHeight;
        const top = placeBelow
            ? tutorialRect.top + tutorialRect.height + gap
            : Math.max(16, tutorialRect.top - 220 - gap);
        const preferredLeft = tutorialRect.left;
        const maxLeft = Math.max(16, viewportWidth - cardWidth - 16);
        const left = Math.min(Math.max(16, preferredLeft), maxLeft);
        return {
            position: "fixed",
            top,
            left,
            width: cardWidth,
            zIndex: 1062,
            boxShadow: "0 16px 40px rgba(0, 0, 0, 0.35)",
            transition: "top 160ms ease, left 160ms ease",
        };
    };

    // chart data
    const seasonChartData = useMemo(() => {
        const shotsByGame = {};
        playerShots.forEach((shot) => {
            const gameId = shot.game || shot.gameId;
            if (!gameId) return;
            if (!shotsByGame[gameId]) shotsByGame[gameId] = [];
            shotsByGame[gameId].push(shot);
        });

        const rows = [
            [
                { label: "Game", type: "string" },
                ...categories.map((category) => ({
                    label: category.label,
                    type: "number",
                })),
            ],
        ];

        Object.keys(shotsByGame)
            .sort((firstId, secondId) => {
                const firstGame = gamesDataResponse.entries.find(
                    (game) => game._id === firstId,
                );
                const secondGame = gamesDataResponse.entries.find(
                    (game) => game._id === secondId,
                );
                return (
                    new Date(firstGame?.GameDate) -
                    new Date(secondGame?.GameDate)
                );
            })
            .forEach((gameId) => {
                const shots = shotsByGame[gameId];
                const label = gameMap[gameId] || gameId;
                const row = [label];

                categories.forEach(({ filter }) => {
                    const matchingShots = shots.filter(filter);
                    if (matchingShots.length === 0) {
                        row.push(null);
                        return;
                    }

                    const points = matchingShots.reduce(
                        (sum, shot) => sum + outcomePoints(shot.shotOutcome),
                        0,
                    );
                    const expected = matchingShots.reduce(
                        (sum, shot) => sum + (shot.expectedScore || 0),
                        0,
                    );
                    row.push(
                        expected > 0
                            ? Number((points / expected).toFixed(2))
                            : 0,
                    );
                });

                rows.push(row);
            });

        return rows;
    }, [playerShots, gamesDataResponse, categories, gameMap, outcomePoints]);

    // chart options
    const seriesOptions = useMemo(() => {
        const seriesHasPoint = categories.map(({ filter }) =>
            playerShots.some((shot) => filter(shot)),
        );
        const options = {};
        seriesHasPoint.forEach((visible, index) => {
            if (!visible) options[index] = { visibleInLegend: false };
        });
        return options;
    }, [categories, playerShots]);

    const yAxisOptions = useMemo(() => {
        const values = [];
        for (
            let rowIndex = 1;
            rowIndex < seasonChartData.length;
            rowIndex += 1
        ) {
            const row = seasonChartData[rowIndex];
            for (
                let columnIndex = 1;
                columnIndex < row.length;
                columnIndex += 1
            ) {
                const value = row[columnIndex];
                if (typeof value === "number") values.push(value);
            }
        }

        const maxRaw = values.length ? Math.max(...values) : 0;
        const maxValue = maxRaw === 0 ? 1 : maxRaw;
        const roughStep = maxValue / 5;
        const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep || 1)));
        const niceStep =
            [1, 2, 5, 10]
                .map((n) => n * magnitude)
                .find((n) => n >= roughStep) ||
            roughStep ||
            1;
        const step = Number(niceStep.toFixed(4));
        const maxTick = maxRaw === 0 ? 1 : maxValue + step;

        const ticks = [];
        for (
            let tickValue = 0;
            tickValue <= maxTick + 1e-9;
            tickValue += step
        ) {
            ticks.push(Number(tickValue.toFixed(4)));
        }

        return {
            viewWindowMode: "explicit",
            viewWindow: { min: 0, max: maxTick },
            ticks,
        };
    }, [seasonChartData]);

    const handleDownloadPng = () => {
        if (!chartWrapper) return;
        const uri = chartWrapper.getChart().getImageURI();
        const safePlayer = String(seasonPlayer || "All")
            .trim()
            .replace(/[^\w-]+/g, "_");
        const filename = `player-season-${safePlayer}.png`;
        const link = document.createElement("a");
        link.href = uri;
        link.download = filename;
        link.click();
    };

    // loading/errors
    if (shotsLoading || summaryLoading) return <Loader />;
    if (shotsError)
        return <Message variant="danger">Failed to load shots</Message>;
    if (summaryError)
        return (
            <Message variant="danger">Failed to load player summary</Message>
        );

    // render
    return (
        <Container fluid className="py-4 app-section-page">
            <title>Player Analysis | ShotArc</title>
            <ScreenTabs items={ANALYSIS_TABS} />
            <div
                className="d-flex align-items-center justify-content-between gap-3 mb-3"
                ref={pageTitleRef}
            >
                <h2 className="app-page-title mb-0">Player Analysis</h2>
                <Button
                    variant="outline-light"
                    size="sm"
                    className="d-inline-flex align-items-center gap-2"
                    onClick={startTutorial}
                >
                    <FaQuestionCircle />
                    Help Tour
                </Button>
            </div>

            <Card className="border-warning mb-4" ref={summaryCardRef}>
                <Card.Header
                    className="bg-warning text-dark d-flex justify-content-between align-items-center"
                    style={{ cursor: "pointer" }}
                    onClick={() => setSummaryOpen(!summaryOpen)}
                >
                    <strong>Player Summary</strong>
                    {summaryOpen ? <FaChevronUp /> : <FaChevronDown />}
                </Card.Header>
                {summaryOpen && (
                    <Card.Body className="bg-dark text-white">
                        {playerSummary.length === 0 ? (
                            <Alert className="app-empty-state mb-0" ref={noPlayerDataRef}>
                                No player data found.
                            </Alert>
                        ) : (
                            <>
                        <Row className="d-flex justify-content-between align-items-center">
                            <Col md={3}>
                                <Form.Group className="d-flex justify-content-around align-items-center">
                                    <Form.Label className="m-2 fw-bold">
                                        Show
                                    </Form.Label>
                                    <Form.Select
                                        size="sm"
                                        aria-label="Show Results"
                                        value={pageSize}
                                        onChange={(event) => {
                                            setPageSize(
                                                Number(event.target.value),
                                            );
                                            setCurrentPage(1);
                                        }}
                                    >
                                        {[5, 10, 20, 50].map((value) => (
                                            <option key={value} value={value}>
                                                {value}
                                            </option>
                                        ))}
                                    </Form.Select>
                                    <Form.Label className="m-2 fw-bold">
                                        Entries
                                    </Form.Label>
                                </Form.Group>
                            </Col>

                            <Col md={4}>
                                <Form.Group className="d-flex justify-content-around align-items-center">
                                    <Form.Label className="m-3 fw-bold">
                                        Search:
                                    </Form.Label>
                                    <Form.Control
                                        placeholder="Player Name"
                                        value={searchQuery}
                                        onChange={(event) => {
                                            setSearchQuery(event.target.value);
                                            setCurrentPage(1);
                                        }}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Table striped bordered hover responsive className="app-table">
                            <thead className="table-dark">
                                <tr>
                                    <th></th>
                                    {columns.map((key) => (
                                        <th
                                            key={key}
                                            onClick={() => handleSort(key)}
                                        >
                                            {headerLabels[key] || capitalize(key)}
                                            {/*  control sort chevron */}
                                            <span
                                                style={{
                                                    width: "1rem",
                                                    visibility:
                                                        key === sortField
                                                            ? "visible"
                                                            : "hidden",
                                                    display: "inline-block",
                                                }}
                                            >
                                                {sortDirection === 1 ? (
                                                    <FaChevronUp />
                                                ) : (
                                                    <FaChevronDown />
                                                )}
                                            </span>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {playerSummary.map((player, i) => (
                                    <tr
                                        key={player.player}
                                        ref={
                                            i === 0
                                                ? firstPlayerRowRef
                                                : undefined
                                        }
                                        className={seasonPlayer === player.player ? "table-active" : ""}
                                        style={{ cursor: "pointer" }}
                                        onClick={() =>
                                            handleSelectPlayer(player.player)
                                        }
                                    >
                                        <td>
                                            {i +
                                                1 +
                                                pageSize * (currentPage - 1)}
                                        </td>
                                        <td>{player.player}</td>
                                        <td>{player.attempts}</td>
                                        <td>{player.scored}</td>
                                        <td>{player.points}</td>
                                        <td>{player.xPoints}</td>
                                        <td>{player.efficiency}</td>
                                        <td>{player.conversionRate}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>

                        <Row className="d-flex justify-content-around">
                            <Col>
                                <p>
                                    Showing {playerData.length} out of{" "}
                                    {totalRows} results
                                </p>
                            </Col>

                            <Col>
                                <Pagination className="justify-content-center app-pagination">
                                    <Pagination.Prev
                                        disabled={currentPage === 1}
                                        onClick={() =>
                                            setCurrentPage(currentPage - 1)
                                        }
                                    />
                                    {[
                                        ...Array(
                                            Math.ceil(totalRows / pageSize),
                                        ),
                                    ].map((_, index) => (
                                        <Pagination.Item
                                            key={index}
                                            active={currentPage === index + 1}
                                            onClick={() =>
                                                setCurrentPage(index + 1)
                                            }
                                        >
                                            {index + 1}
                                        </Pagination.Item>
                                    ))}
                                    <Pagination.Next
                                        disabled={
                                            currentPage ===
                                            Math.ceil(totalRows / pageSize)
                                        }
                                        onClick={() =>
                                            setCurrentPage(currentPage + 1)
                                        }
                                    />
                                </Pagination>
                            </Col>
                        </Row>
                            </>
                        )}
                    </Card.Body>
                )}
            </Card>

            <Card className="border-warning mb-4" ref={gameSummaryCardRef}>
                <Card.Header
                    className="bg-warning text-dark d-flex justify-content-between align-items-center"
                    style={{ cursor: "pointer" }}
                    onClick={() => setGameSummaryOpen((open) => !open)}
                >
                    <strong>Game Summary</strong>
                    {gameSummaryOpen ? <FaChevronUp /> : <FaChevronDown />}
                </Card.Header>
                {gameSummaryOpen && (
                    <Card.Body className="bg-dark text-white">
                        {seasonPlayer === "All" ? (
                            <Alert className="app-empty-state mb-0" ref={noSelectionAlertRef}>
                                Select a player from the summary table to view
                                their game summary.
                            </Alert>
                        ) : (
                            <>
                                <Row className="mb-3">
                                    <Col md={4}>
                                        <Form.Label>Search</Form.Label>
                                        <Form.Control
                                            placeholder="Search by Team"
                                            value={gameSearch}
                                            onChange={(event) => {
                                                setGameSearch(
                                                    event.target.value,
                                                );
                                                setGamePage(1);
                                            }}
                                        />
                                    </Col>

                                    <Col md={2}>
                                        <Form.Label>Entries</Form.Label>
                                        <Form.Select
                                            value={gameLimit}
                                            onChange={(event) => {
                                                setGameLimit(
                                                    Number(event.target.value),
                                                );
                                                setGamePage(1);
                                            }}
                                        >
                                            {[1, 5, 10, 20, 50].map((value) => (
                                                <option
                                                    key={value}
                                                    value={value}
                                                >
                                                    {value}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Col>
                                </Row>

                                <p className="text-light mb-3">
                                    Showing {playerGamesResponse.entries.length}{" "}
                                    of {playerGamesResponse.total} games
                                </p>

                                {gamesLoading ? (
                                    <Loader />
                                ) : gamesError ? (
                                    <Message variant="danger">
                                        {gamesError.data?.message ||
                                            "Error loading games"}
                                    </Message>
                                ) : (
                                    <>
                                        <Table
                                            striped
                                            bordered
                                            hover
                                            responsive
                                            className="app-table"
                                        >
                                            <thead className="table-warning">
                                                <tr>
                                                    {[
                                                        {
                                                            label: "Date",
                                                            field: "gameDate",
                                                        },
                                                        {
                                                            label: "Game",
                                                            field: "game",
                                                        },
                                                        {
                                                            label: "Shots",
                                                            field: "shots",
                                                        },
                                                        {
                                                            label: "Scored",
                                                            field: "scored",
                                                        },
                                                        {
                                                            label: "Points",
                                                            field: "points",
                                                        },
                                                        {
                                                            label: "xPoints",
                                                            field: "xPoints",
                                                        },
                                                        {
                                                            label: "Efficiency",
                                                            field: "efficiency",
                                                        },
                                                        {
                                                            label: "Conversion",
                                                            field: "conversionRate",
                                                        },
                                                    ].map(
                                                        ({ label, field }) => (
                                                            <th
                                                                key={field}
                                                                onClick={() =>
                                                                    handleGameSort(
                                                                        field,
                                                                    )
                                                                }
                                                                style={{
                                                                    cursor: "pointer",
                                                                }}
                                                            >
                                                                {label}
                                                                <span
                                                                    style={{
                                                                        width: "1rem",
                                                                        visibility:
                                                                            field ===
                                                                            gameSortField
                                                                                ? "visible"
                                                                                : "hidden",
                                                                        display:
                                                                            "inline-block",
                                                                    }}
                                                                >
                                                                    {gameSortDirection ===
                                                                    1 ? (
                                                                        <FaChevronUp />
                                                                    ) : (
                                                                        <FaChevronDown />
                                                                    )}
                                                                </span>
                                                            </th>
                                                        ),
                                                    )}
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {playerGamesResponse.entries
                                                    .length === 0 ? (
                                                    <tr>
                                                        <td
                                                            colSpan="7"
                                                            className="text-center"
                                                        >
                                                            No games found
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    playerGamesResponse.entries.map(
                                                        (game) => (
                                                            <tr
                                                                key={
                                                                    game.gameId
                                                                }
                                                            >
                                                                <td>
                                                                    {formatGameDate(
                                                                        game.gameDate,
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    {game.game}
                                                                </td>
                                                                <td>
                                                                    {game.shots}
                                                                </td>
                                                                <td>
                                                                    {
                                                                        game.scored
                                                                    }
                                                                </td>
                                                                <td>
                                                                    {
                                                                        game.points
                                                                    }
                                                                </td>
                                                                <td>
                                                                    {
                                                                        game.xPoints
                                                                    }
                                                                </td>
                                                                <td>
                                                                    {
                                                                        game.efficiency
                                                                    }
                                                                </td>
                                                                <td>
                                                                    {
                                                                        game.shots > 0
                                                                            ? `${Math.round((game.scored / game.shots) * 100)}%`
                                                                            : ""
                                                                    }
                                                                </td>
                                                            </tr>
                                                        ),
                                                    )
                                                )}
                                            </tbody>
                                        </Table>
                                        <Pagination className="justify-content-center mt-3 app-pagination">
                                            <Pagination.Prev
                                                disabled={gamePage === 1}
                                                onClick={() =>
                                                    setGamePage(gamePage - 1)
                                                }
                                            />
                                            {[
                                                ...Array(
                                                    playerGamesResponse.pages ||
                                                        1,
                                                ),
                                            ].map((_, index) => (
                                                <Pagination.Item
                                                    key={index}
                                                    active={
                                                        gamePage === index + 1
                                                    }
                                                    onClick={() =>
                                                        setGamePage(index + 1)
                                                    }
                                                >
                                                    {index + 1}
                                                </Pagination.Item>
                                            ))}
                                            <Pagination.Next
                                                disabled={
                                                    gamePage ===
                                                    playerGamesResponse.pages
                                                }
                                                onClick={() =>
                                                    setGamePage(gamePage + 1)
                                                }
                                            />
                                        </Pagination>
                                    </>
                                )}
                            </>
                        )}
                    </Card.Body>
                )}
            </Card>

            <Card className="border-warning mb-4" ref={mapCardRef}>
                <Card.Header
                    className="bg-warning text-dark d-flex justify-content-between align-items-center"
                    style={{ cursor: "pointer" }}
                    onClick={() => setMapOpen((open) => !open)}
                >
                    <strong>Player Shot Map</strong>
                    {mapOpen ? <FaChevronUp /> : <FaChevronDown />}
                </Card.Header>
                {mapOpen && (
                    <Card.Body className="bg-dark text-white">
                        {seasonPlayer === "All" ? (
                            <Alert className="app-empty-state mb-0">
                                Select a player from the summary table to view
                                their shot map.
                            </Alert>
                        ) : (
                            <Row>
                                <Col md={3} className="filters">
                                    <Form.Group className="mb-2">
                                        <Form.Label>Shot Type</Form.Label>
                                        <div className="map-radio-grid map-radio-grid-2">
                                            {orderedMapShotTypeOptions.map((value) => (
                                                <Form.Check
                                                    key={value}
                                                    type="radio"
                                                    id={`player-map-type-${value}`}
                                                    name="playerMapType"
                                                    label={formatShotTypeLabel(value)}
                                                    value={value}
                                                    checked={
                                                        mapShotType === value
                                                    }
                                                    onChange={(event) =>
                                                        setMapShotType(
                                                            event.target.value,
                                                        )
                                                    }
                                                />
                                            ))}
                                        </div>
                                    </Form.Group>
                                    <Form.Group className="mb-2">
                                        <Form.Label>Game</Form.Label>
                                        <Form.Select
                                            value={mapGame}
                                            onChange={(event) =>
                                                setMapGame(event.target.value)
                                            }
                                        >
                                            {mapGameOptions.map((gameId) => (
                                                <option
                                                    key={gameId}
                                                    value={gameId}
                                                >
                                                    {gameId === "All"
                                                        ? "All"
                                                        : gameMap[gameId] ||
                                                          gameId}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                    <Form.Group className="mb-2">
                                        <Form.Label>Game Half</Form.Label>
                                        <div>
                                            {["All", "1", "2", "3"].map((value) => (
                                                <Form.Check
                                                    key={value}
                                                    inline
                                                    type="radio"
                                                    id={`player-map-half-${value}`}
                                                    name="playerMapHalf"
                                                    label={value}
                                                    value={value}
                                                    checked={
                                                        mapGameHalf === value
                                                    }
                                                    onChange={(event) =>
                                                        setMapGameHalf(
                                                            event.target.value,
                                                        )
                                                    }
                                                />
                                            ))}
                                        </div>
                                    </Form.Group>
                                    <Form.Group className="mb-2">
                                        <Form.Label>Set Play</Form.Label>
                                        <Form.Select
                                            value={mapSetPlay}
                                            onChange={(event) =>
                                                setMapSetPlay(
                                                    event.target.value,
                                                )
                                            }
                                        >
                                            {mapSetPlayOptions.map((value) => (
                                                <option key={value}>
                                                    {value}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                    <Form.Group className="mb-2">
                                        <Form.Label>Outcome</Form.Label>
                                        <Form.Select
                                            value={mapShotOutcome}
                                            onChange={(event) =>
                                                setMapShotOutcome(
                                                    event.target.value,
                                                )
                                            }
                                        >
                                            {mapShotOutcomeOptions.map(
                                                (value) => (
                                                    <option key={value}>
                                                        {value}
                                                    </option>
                                                ),
                                            )}
                                        </Form.Select>
                                    </Form.Group>
                                    <Form.Group className="mb-2">
                                        <Form.Label>Pressure</Form.Label>
                                        <div className="map-radio-grid map-radio-grid-2">
                                            {orderedMapShotPressureOptions.map(
                                                (value) => (
                                                    <Form.Check
                                                        key={value}
                                                        type="radio"
                                                        id={`player-map-pressure-${value}`}
                                                        name="playerMapPressure"
                                                        label={value}
                                                        value={value}
                                                        checked={
                                                            mapShotPressure ===
                                                            value
                                                        }
                                                        onChange={(event) =>
                                                            setMapShotPressure(
                                                                event.target
                                                                    .value,
                                                            )
                                                        }
                                                    />
                                                ),
                                            )}
                                        </div>
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Method</Form.Label>
                                        <div className="map-radio-grid map-radio-grid-2">
                                            {orderedMapShotMethodOptions.map(
                                                (value) => (
                                                    <Form.Check
                                                        key={value}
                                                        type="radio"
                                                        id={`player-map-method-${value}`}
                                                        name="playerMapMethod"
                                                        label={value}
                                                        value={value}
                                                        checked={
                                                            mapShotMethod ===
                                                            value
                                                        }
                                                        onChange={(event) =>
                                                            setMapShotMethod(
                                                                event.target
                                                                    .value,
                                                            )
                                                        }
                                                    />
                                                ),
                                            )}
                                        </div>
                                    </Form.Group>
                                    <Button
                                        variant="outline-light"
                                        onClick={handleDownloadPlayerMap}
                                        className="w-100"
                                    >
                                        Save as PNG
                                    </Button>
                                </Col>

                                <Col md={9}>
                                    <div
                                        ref={mapExportRef}
                                        className="map-export"
                                    >
                                        <Row className="g-0">
                                            <Col
                                                md={9}
                                                className="pitch-wrapper"
                                            >
                                                <ShotMapCanvas
                                                    markers={mapMarkers}
                                                    summaryText={`${seasonPlayer}: ${mapSummary.total} Shots, xPoints ${mapSummary.xp.toFixed(
                                                        2,
                                                    )}, Points ${mapSummary.pts}, Efficiency ${mapSummary.eff}, Conversion ${Math.round(mapSummary.conversionRate * 100)}%`}
                                                />
                                                {filteredPlayerShots.length ===
                                                    0 && (
                                                    <Alert
                                                        className="app-empty-state mt-3 mb-0"
                                                    >
                                                        No shots match these
                                                        filters.
                                                    </Alert>
                                                )}
                                            </Col>

                                            <Col md={3}>
                                                <ShotMapLegend />
                                            </Col>
                                        </Row>
                                    </div>
                                </Col>
                            </Row>
                        )}
                    </Card.Body>
                )}
            </Card>

            <Card className="border-warning mb-4" ref={seasonChartCardRef}>
                <Card.Header
                    className="bg-warning text-dark d-flex justify-content-between align-items-center"
                    style={{ cursor: "pointer" }}
                    onClick={() => setOpenSeason((o) => !o)}
                >
                    <strong>Player Season Chart</strong>
                    {openSeason ? <FaChevronUp /> : <FaChevronDown />}
                </Card.Header>
                {openSeason &&  (
                    <Card.Body className="bg-dark text-white">
                        {
                        seasonPlayer === "All" ?
                        <Alert className="app-empty-state mb-0">
                            Select a player from the summary table to view
                            their shot map.
                        </Alert> :
                        seasonPlayer !== "All" && seasonChartData.length > 1 ? (
                            <>
                                <div className="d-flex justify-content-end mb-2">
                                    <button
                                        type="button"
                                        className="btn btn-outline-light btn-sm"
                                        onClick={handleDownloadPng}
                                        disabled={!chartWrapper}
                                    >
                                        Save as PNG
                                    </button>
                                </div>
                                <Chart
                                    chartType="LineChart"
                                    width="100%"
                                    height="400px"
                                    loader={<div>Loading...</div>}
                                    data={seasonChartData}
                                    chartEvents={[
                                        {
                                            eventName: "ready",
                                            callback: ({
                                                chartWrapper: wrapper,
                                            }) => {
                                                setChartWrapper(wrapper);
                                            },
                                        },
                                    ]}
                                    options={{
                                        curveType: "none",
                                        pointSize: 5,
                                        colors: [
                                            "#28a745",
                                            "#6f42c1",
                                            "#dc3545",
                                            "#0d6efd",
                                        ],
                                        hAxis: {
                                            title: "Game",
                                            titleTextStyle: { italic: false },
                                        },
                                        vAxis: {
                                            title: "Shot Efficiency",
                                            titleTextStyle: { italic: false },
                                            minValue: 0,
                                            ...yAxisOptions,
                                        },
                                        series: seriesOptions,
                                        legend: { position: "right" },
                                    }}
                                />
                            </>
                        ) : (
                            <Alert className="app-empty-state mb-0">No shot data for selected player</Alert>
                        )}
                    </Card.Body>
                    
                    
                )}
            </Card>
            {showTutorial && (
                <>
                    {tutorialRect ? (
                        <>
                            <div
                                onClick={closeTutorial}
                                style={{
                                    position: "fixed",
                                    top: 0,
                                    left: 0,
                                    width: "100vw",
                                    height: tutorialRect.top,
                                    background: "rgba(8, 12, 18, 0.72)",
                                    zIndex: 1060,
                                }}
                            />
                            <div
                                onClick={closeTutorial}
                                style={{
                                    position: "fixed",
                                    top: tutorialRect.top,
                                    left: 0,
                                    width: tutorialRect.left,
                                    height: tutorialRect.height,
                                    background: "rgba(8, 12, 18, 0.72)",
                                    zIndex: 1060,
                                }}
                            />
                            <div
                                onClick={closeTutorial}
                                style={{
                                    position: "fixed",
                                    top: tutorialRect.top,
                                    left:
                                        tutorialRect.left + tutorialRect.width,
                                    width: `calc(100vw - ${tutorialRect.left + tutorialRect.width}px)`,
                                    height: tutorialRect.height,
                                    background: "rgba(8, 12, 18, 0.72)",
                                    zIndex: 1060,
                                }}
                            />
                            <div
                                onClick={closeTutorial}
                                style={{
                                    position: "fixed",
                                    top:
                                        tutorialRect.top + tutorialRect.height,
                                    left: 0,
                                    width: "100vw",
                                    height: `calc(100vh - ${tutorialRect.top + tutorialRect.height}px)`,
                                    background: "rgba(8, 12, 18, 0.72)",
                                    zIndex: 1060,
                                }}
                            />
                            <div
                                style={{
                                    position: "fixed",
                                    top: tutorialRect.top,
                                    left: tutorialRect.left,
                                    width: tutorialRect.width,
                                    height: tutorialRect.height,
                                    border: "2px solid #f59e0b",
                                    borderRadius: 12,
                                    pointerEvents: "none",
                                    zIndex: 1061,
                                    transition:
                                        "top 160ms ease, left 160ms ease, width 160ms ease, height 160ms ease",
                                }}
                            />
                            <Card
                                className="border-warning"
                                style={getTutorialCardStyle()}
                            >
                                <Card.Header className="bg-warning text-dark d-flex justify-content-between align-items-center">
                                    <strong>
                                        {tutorialStepIndex + 1} /{" "}
                                        {tutorialSteps.length}{" "}
                                        {tutorialSteps[tutorialStepIndex].title}
                                    </strong>
                                    <Button
                                        variant="link"
                                        className="text-dark p-0 text-decoration-none fw-semibold"
                                        onClick={closeTutorial}
                                    >
                                        Close
                                    </Button>
                                </Card.Header>
                                <Card.Body className="bg-dark text-white">
                                    <p className="mb-3">
                                        {
                                            tutorialSteps[tutorialStepIndex]
                                                .body
                                        }
                                    </p>
                                    <div className="d-flex justify-content-between gap-2">
                                        <Button
                                            variant="outline-light"
                                            size="sm"
                                            disabled={
                                                tutorialStepIndex === 0
                                            }
                                            onClick={prevTutorialStep}
                                        >
                                            Back
                                        </Button>
                                        <Button
                                            variant="warning"
                                            size="sm"
                                            className="text-dark fw-semibold"
                                            onClick={nextTutorialStep}
                                        >
                                            {tutorialStepIndex ===
                                            tutorialSteps.length - 1
                                                ? "Finish"
                                                : "Next"}
                                        </Button>
                                    </div>
                                </Card.Body>
                            </Card>
                        </>
                    ) : (
                        <div
                            onClick={closeTutorial}
                            style={{
                                position: "fixed",
                                inset: 0,
                                background: "rgba(8, 12, 18, 0.72)",
                                zIndex: 1060,
                            }}
                        />
                    )}
                </>
            )}
        </Container>
    );
};

export default PlayerAnalysisScreen;
